"use client";

import { ReactNode } from "react";
import { DateRangeValue } from "@/lib/constants";
import { TimeOfDay } from "@/lib/utils";
import { GenreFilter } from "./genre-filter";
import { DateFilter } from "./date-filter";
import { TimeOfDayFilter } from "./time-of-day-filter";
import { PlayIcon } from "@/components/spotify/play-icon";

interface FilterBarProps {
  genres: string[];
  genreCounts?: Map<string, number>;
  genreFilteredCount?: number;
  otherSubGenres: string[];
  selectedGenres: string[];
  onGenresChange: (genres: string[]) => void;
  genreMatchMode: "or" | "and";
  onMatchModeChange: (mode: "or" | "and") => void;
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  customStartDate: string | null;
  customEndDate: string | null;
  onCustomStartDateChange: (date: string | null) => void;
  onCustomEndDateChange: (date: string | null) => void;
  selectedTime: TimeOfDay | null;
  onTimeChange: (time: TimeOfDay | null) => void;
  onlyWithAudio?: boolean;
  onOnlyWithAudioChange?: (value: boolean) => void;
  hideAudioFilter?: boolean;
  /** Desktop-only slot at the right edge, bottom-aligned with the genre row */
  viewSwitcher?: ReactNode;
  genreTrailing?: ReactNode;
  onJukeboxPlay?: () => void;
  jukeboxReady?: boolean;
}

export function FilterBar({
  genres,
  genreCounts,
  genreFilteredCount,
  otherSubGenres,
  selectedGenres,
  onGenresChange,
  genreMatchMode,
  onMatchModeChange,
  dateRange,
  onDateRangeChange,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  selectedTime,
  onTimeChange,
  onlyWithAudio,
  onOnlyWithAudioChange,
  hideAudioFilter,
  viewSwitcher,
  genreTrailing,
  onJukeboxPlay,
  jukeboxReady,
}: FilterBarProps) {
  const audioButton = (
    <button
      onClick={() => onOnlyWithAudioChange?.(!onlyWithAudio)}
      className={`flex cursor-pointer items-center gap-2.5 rounded-full border px-5 py-2.5 font-mono text-sm transition-colors ${
        onlyWithAudio
          ? "border-[#1DB954]/50 bg-[#1DB954]/10 text-[#1DB954]"
          : "border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-neutral-300"
      }`}
    >
      <PlayIcon className="h-5 w-5" />
      AUDIO AVAILABLE
    </button>
  );

  return (
    <div className="mb-1 sm:flex sm:items-end sm:gap-4">
      <div className="min-w-0 flex-1 space-y-3">
        <DateFilter
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomStartDateChange={onCustomStartDateChange}
          onCustomEndDateChange={onCustomEndDateChange}
          trailingValues={["week", "month", "custom"]}
        >
          <TimeOfDayFilter
            selectedTime={selectedTime}
            onTimeChange={onTimeChange}
          />
        </DateFilter>
        {genres.length > 0 && (
          <GenreFilter
            genres={genres}
            genreCounts={genreCounts}
            genreFilteredCount={genreFilteredCount}
            otherSubGenres={otherSubGenres}
            selectedGenres={selectedGenres}
            onGenresChange={onGenresChange}
            genreMatchMode={genreMatchMode}
            onMatchModeChange={onMatchModeChange}
            onJukeboxPlay={onJukeboxPlay}
            jukeboxReady={jukeboxReady}
          >
            {genreTrailing}
          </GenreFilter>
        )}
      </div>
      {viewSwitcher && (
        <div className="hidden shrink-0 sm:block">{viewSwitcher}</div>
      )}
      {!hideAudioFilter && (
        <>
          <div className="mt-3 shrink-0 sm:mt-0 hidden sm:block">{audioButton}</div>
          <div className="mt-3 sm:hidden">{audioButton}</div>
        </>
      )}
    </div>
  );
}
